import { memo, type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { PublicBoardCustomFieldsPanel } from '~/components/PublicBoard/components/PublicBoardCustomFieldsPanelContent/PublicBoardCustomFieldsPanel';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PublicBoardCover } from '~/components/v2/components/board/board-cover/PublicBoardCover';
import { PublicBoardContent, type PublicBoardContentProps } from '~/components/v2/components/board/PublicBoardContent';
import { PublicBoardHeader } from '~/components/v2/components/board/PublicBoardHeader';
import { PublicBoardTopbar } from '~/components/v2/components/board/PublicBoardTopbar';
import { useResetViewSelectAllOnBoardIdChange } from '~/components/v2/hooks/useResetViewSelectAllOnBoardIdChange';
import { type PublicBoardContextValue, PublicBoardProvider } from '~/providers/PublicBoardProvider';
import { centralizedBoardIdSelector, centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export type PublicBoardProps = {
  children?: ReactNode;
  /**
   * The topbar's breadcrumb trail, supplied by the hosting area (board-only on a standalone share,
   * collection-prefixed inside a collection). PublicBoard stays area-agnostic — it only forwards the
   * slot to the topbar in the right layout position.
   */
  breadcrumbs: ReactNode;
  /**
   * The topbar's trailing content (the standalone board share's "Save all" download menu). Absent
   * inside the collection tree, where that viewer action does not belong.
   */
  topbarTrailingContent?: ReactNode;
  // The hosting surface's items views — REQUIRED composed components, not renderers: each surface
  // builds its own gallery/table view (its cards, its fetch scope — the board page's public arrows
  // over an unscoped fetch, the collection tree's contract renderers over a collection-scoped
  // fetch). The shell only seats them in the view-type switch and injects the scroll plumbing
  // (see PublicBoardItemsViewProps). No fallback lives here: an items view is a surface's
  // behavior, and this tree is told it, never defaults it.
} & Pick<PublicBoardContentProps, 'GalleryView' | 'TableView'> &
  // The board tree's composition contract members are its own required props: PublicBoard mounts
  // PublicBoardProvider internally, so mounting the tree without a surface's bindings is a type
  // error (see PublicBoardProvider).
  PublicBoardContextValue;

export const PublicBoard = memo(
  ({
    assetModalBreadcrumbPrefix,
    children,
    breadcrumbs,
    GalleryView,
    onNavigateToBoard,
    TableView,
    topbarTrailingContent,
  }: PublicBoardProps) => {
    const board = useSelector(centralizedBoardSelector);
    const boardId = useSelector(centralizedBoardIdSelector);
    const { permissions } = usePublicPermissions();

    useResetViewSelectAllOnBoardIdChange({ boardId });

    const canViewCustomFields = canSeeBoardCustomFields(permissions);

    if (!board) {
      return null;
    }

    return (
      <PublicBoardProvider
        assetModalBreadcrumbPrefix={assetModalBreadcrumbPrefix}
        onNavigateToBoard={onNavigateToBoard}
      >
        <PublicBoardTopbar breadcrumbs={breadcrumbs} trailingContent={topbarTrailingContent} />
        <PublicBoardCover key={board.id} board={board} />
        <PublicBoardHeader />
        <PublicBoardContent GalleryView={GalleryView} TableView={TableView} />
        {canViewCustomFields && <PublicBoardCustomFieldsPanel />}
        {children}
      </PublicBoardProvider>
    );
  },
);

PublicBoard.displayName = 'PublicBoard';
